import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Loader2, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { RBACContext } from '@/contexts/RBACContext';
import { useUniversalSearch } from '@/hooks/useUniversalSearch';
import { useSearchParseContext } from '@/hooks/useSearchParseContext';
import { ENTITY_LABEL, type SearchV1Result } from '@/lib/searchApi';
import {
  ENTITY_CHIP_LABEL, chooseDateField, chooseEntity, pickDate, removeChip, type Chip, type SearchEntity,
} from '@/lib/search/searchFilterChips';
import { SearchDateRangePicker } from '@/components/search/SearchDateRangePicker';
import { SearchFilterChips } from '@/components/search/SearchFilterChips';
import { openSalesResult } from '@/lib/handoff/openSalesResult';
import { cn } from '@/lib/utils';

const optionId = (r: SearchV1Result) => `search-result-${r.entity}-${r.id}`;

/** What the entity chip's menu offers: what App search returns for this user. */
const TENANT_ENTITIES: readonly SearchEntity[] = ['unit', 'group', 'site', 'company'];
/**
 * Staff also search App's internal records and Sales' (the server federates to
 * Sales for them, T-160), so an AI reading can name any of these (T-151).
 */
const INTERNAL_ENTITIES: readonly SearchEntity[] = [
  ...TENANT_ENTITIES, 'work_order', 'damage_finding', 'checkin', 'document',
  'contact', 'quote', 'rental', 'lead', 'purchase_order', 'vendor', 'vendor_contact',
];
/** Sales-owned: searched only for staff; anyone else gets none of these. */
const SALES_ENTITIES = new Set<SearchEntity>(['contact', 'quote', 'rental', 'lead', 'purchase_order', 'vendor', 'vendor_contact']);

/**
 * Where a result opens. An App-owned result always navigates within THIS
 * deployment by its path: the server builds URLs on the canonical app host,
 * so comparing origins would send a DEMO or preview user to PROD in a new tab.
 * Only another app's result (Sales, later) opens its own absolute URL.
 */
function toDestination(r: SearchV1Result): { inApp: true; path: string } | { inApp: false; href: string } {
  const url = r.url ?? '';
  try {
    const u = new URL(url, window.location.origin);
    if (r.owner_app === 'app' || u.origin === window.location.origin) {
      return { inApp: true, path: `${u.pathname}${u.search}${u.hash}` };
    }
    return { inApp: false, href: u.toString() };
  } catch {
    return { inApp: true, path: url };
  }
}

/**
 * Universal search (P4, spec §3.5): a search GLYPH in the header (the map
 * already has its own search field, so no second box) + ⌘K / Ctrl-K, opening a
 * modal over
 * GET /admin/search/v1. Results grouped by type; arrows + Enter; a result the
 * caller may see but not open on web (e.g. a unit for a customer admin) is
 * listed but not actionable.
 */
export function UniversalSearch() {
  const navigate = useNavigate();
  const [open, setOpenState] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerOpenerRef = useRef<HTMLElement | null>(null);
  const parseCtx = useSearchParseContext();
  const isInternal = useContext(RBACContext)?.isInternal ?? false;
  const {
    query, setQuery, groups, loading, error, recent, rememberQuery, salesUnavailable, salesPending,
    active: isActive, filters, chips, timeZone, editFilters, refreshClock,
    answered, understanding, understandNow, dismissReading,
  } = useUniversalSearch(parseCtx, { sales: isInternal }); // Sales is searched for staff only
  // Opening or closing the search also closes the date picker, so it never
  // reappears on its own next time.
  const setOpen = useCallback((next: boolean | ((was: boolean) => boolean)) => {
    setOpenState(next);
    setPickerOpen(false);
  }, []);
  const dateChip = chips.find((c) => c.kind === 'date');
  /**
   * Remove a chip, from its × or the calendar's "Clear date". An AI chip's words
   * can't be put back one by one, so removing one drops the whole reading (T-151).
   */
  const dropChip = useCallback((chip: Chip) => {
    if (chip.ai) dismissReading();
    else editFilters((o, p) => removeChip(o, chip, p));
  }, [dismissReading, editFilters]);
  // Selection is tracked by result key, so a new result set without that key
  // falls back to the first result without resetting state in an effect.
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const flat = useMemo(() => groups.flatMap((g) => g.results), [groups]);
  const actionable = useMemo(() => flat.filter((r) => r.actionable && r.url), [flat]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  const active = Math.max(0, actionable.findIndex((r) => optionId(r) === activeKey));
  const setActive = (i: number) => setActiveKey(actionable[i] ? optionId(actionable[i]) : null);

  const go = useCallback((r: SearchV1Result) => {
    if (!r.actionable || !r.url) return;
    rememberQuery(query);
    setOpen(false);
    const dest = toDestination(r);
    if (dest.inApp) navigate(dest.path);
    // A Sales result opens Sales signed in (P7, T-165), else its plain URL.
    else openSalesResult(dest.href);
  }, [navigate, query, rememberQuery, setOpen]);

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (actionable.length === 0) {
      // Nothing to open: Enter asks for an AI reading now instead of after the pause (T-151).
      if (e.key === 'Enter') { e.preventDefault(); understandNow(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((active + 1) % actionable.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((active - 1 + actionable.length) % actionable.length); }
    if (e.key === 'Enter') { e.preventDefault(); go(actionable[active]); }
  };

  const activeResult = actionable[active];
  const trimmed = query.trim();
  const searching = isActive;
  const salesOnly = !isInternal && filters.entity && SALES_ENTITIES.has(filters.entity) ? ENTITY_CHIP_LABEL[filters.entity] : null;
  // What the empty list says, in Sales' order (ISS-209). "No matches" only once the answer for
  // the text on screen has arrived and no AI reading is due or on its way. Sales' own leg still
  // on its way says nothing: its section follows.
  let emptyStatus = '';
  if (searching && !error && groups.length === 0) {
    if (loading || !answered) emptyStatus = 'Searching…';
    else if (salesPending) emptyStatus = '';
    else if (understanding) emptyStatus = 'Searching…';
    else if (salesOnly) emptyStatus = `${salesOnly} are in Sales and aren't searched here yet.`;
    else emptyStatus = chips.length > 0 ? 'No matches for these filters.' : `No matches for “${trimmed}”.`;
  }

  return (
    <>
      <button
        type="button"
        aria-label="Search"
        title="Search (⌘K)"
        onClick={() => setOpen(true)}
        className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-accent transition-colors"
      >
        <Search className="h-5 w-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          // Re-read "today" each time the search opens (a tab left open overnight).
          onOpenAutoFocus={() => refreshClock()}
          className="top-[12%] translate-y-0 max-w-xl gap-0 overflow-hidden p-0"
        >
          <DialogTitle className="sr-only">Search</DialogTitle>
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              autoFocus
              role="combobox"
              aria-label="Search"
              aria-expanded={flat.length > 0}
              aria-controls="universal-search-results"
              aria-activedescendant={activeResult ? optionId(activeResult) : undefined}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Search, or try “productions last month”"
              className="h-12 min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
            {(loading || understanding) && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Searching" />}
            <SearchDateRangePicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              value={filters.date}
              entity={filters.entity}
              timeZone={timeZone}
              onPick={(range) => editFilters((o, p) => pickDate(o, range, p))}
              onField={(field) => { if (filters.entity) editFilters((o) => chooseDateField(o, field, filters.entity!)); }}
              onClear={dateChip ? () => dropChip(dateChip) : undefined}
              onCloseAutoFocus={(e) => {
                // Opened from the date chip: give focus back to the chip, not the glyph.
                const opener = pickerOpenerRef.current;
                pickerOpenerRef.current = null;
                if (opener?.isConnected) {
                  e.preventDefault();
                  opener.focus();
                }
              }}
            />
          </div>

          <SearchFilterChips
            chips={chips}
            entity={filters.entity}
            entityOptions={isInternal ? INTERNAL_ENTITIES : TENANT_ENTITIES}
            onRemove={(chip) => {
              dropChip(chip);
              // The focused × is about to unmount: keep keyboard users in the search box.
              inputRef.current?.focus();
            }}
            onEntity={(entity) => editFilters((o, p) => chooseEntity(o, entity, p))}
            onDateOpen={(opener) => { pickerOpenerRef.current = opener; setPickerOpen(true); }}
            dateOpen={pickerOpen}
          />

          <div id="universal-search-results" role="listbox" aria-label="Search results" className="max-h-[60vh] overflow-y-auto py-2">
            {/* Staff get App's and Sales' results separately (ISS-135), so a
                failure is App's part only: Sales' may still show (Seer, #965). */}
            {error && (
              <p role="alert" className="px-4 py-3 text-sm text-[#E0556C]">
                {isInternal ? 'App results couldn\'t load.' : error}
                {isInternal && groups.length > 0 && ' Showing Sales results only.'}
              </p>
            )}

            {!trimmed && !searching && recent.length > 0 && (
              <div role="group" aria-label="Recent searches" className="px-2">
                <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Recent</div>
                {recent.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setQuery(q)}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent"
                  >
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    {q}
                  </button>
                ))}
              </div>
            )}

            {!trimmed && !searching && recent.length === 0 && (
              <p className="px-4 py-3 text-sm text-muted-foreground">
                Search units, productions, sites and companies. Try “productions last month” or “1 room units”.
              </p>
            )}

            {/* One status line, always mounted, so screen readers hear each change of its text. */}
            <p role="status" className={emptyStatus ? 'px-4 py-3 text-sm text-muted-foreground' : 'sr-only'}>
              {emptyStatus}
            </p>

            {groups.map((g) => {
              const label = ENTITY_LABEL[g.entity] ?? g.entity;
              return (
                <div key={g.entity} role="group" aria-label={label} className="px-2 pb-1">
                  <div className="flex items-center justify-between px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    <span>{label}</span>
                    <span className="tabular-nums">{g.results.length}{g.has_more ? '+' : ''}</span>
                  </div>
                  {g.results.map((r) => {
                    const disabled = !r.actionable || !r.url;
                    const selected = activeResult === r;
                    return (
                      <div
                        key={optionId(r)}
                        id={optionId(r)}
                        role="option"
                        aria-selected={selected}
                        aria-disabled={disabled || undefined}
                        onClick={() => go(r)}
                        onMouseEnter={() => { const i = actionable.indexOf(r); if (i >= 0) setActive(i); }}
                        className={cn(
                          'flex items-center justify-between gap-3 rounded px-2 py-1.5 text-sm',
                          disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
                          selected && 'bg-accent',
                        )}
                      >
                        <div className="min-w-0">
                          <div className="truncate text-foreground">{r.title}</div>
                          {r.subtitle && <div className="truncate text-xs text-muted-foreground">{r.subtitle}</div>}
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                          {r.badges.slice(0, 2).map((b) => (
                            <span key={b} className="rounded border border-border px-1.5 py-0.5">{b}</span>
                          ))}
                          {disabled && <span>Open in Pocket</span>}
                        </div>
                      </div>
                    );
                  })}
                  {filters.entity && g.has_more && (
                    <p className="px-2 pt-1 text-xs text-muted-foreground">
                      Showing the first {g.results.length}. Add words to narrow.
                    </p>
                  )}
                </div>
              );
            })}

            {/* ISS-135: Sales' section is appended below App's, so these sit
                where it goes: nothing above the rows on screen moves. */}
            {searching && salesPending && (
              <p className="px-4 pb-1 pt-2 text-xs text-muted-foreground">Searching Sales…</p>
            )}
            {searching && !salesPending && salesUnavailable && (
              <p className="px-4 pb-1 pt-2 text-xs text-muted-foreground">
                Sales results unavailable right now.{groups.length > 0 && ' Showing App results only.'}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
